/* Cart drawer recommendations.

   Replaces the drawer's rail with Shopify's own product recommendations, in
   priority order:

     1. complementary  pairs curated in the Search & Discovery app - "lotion for
                       someone buying toner". Editorial control, and empty until
                       those pairs are set up in admin.
     2. related        Shopify's algorithm over real order history and product
                       similarity - what other customers actually bought with
                       this item.
     3. the existing Liquid rail, left untouched if the two above return nothing,
        so the section is never empty and never collapses.

   Same-brand products are pushed to the BACK of the rail. Someone with the item
   already in their basket has picked that brand; showing more of it converts
   far worse than showing what people actually buy alongside it. Different-brand
   recommendations fill the rail first and same-brand ones only top it up, so
   the rail still fills when a seed returns little else.

   The product page does the opposite and leads with same-brand - see
   assets/quick-add-recommendations.js. Same engine, opposite brand weighting,
   because the question differs: "what else from this brand" before the
   purchase, "what goes with it" after.

   The cards come back as rendered HTML from sections/cart-recommendations
   rather than being rebuilt here, so money formatting, the single-vs-multi
   variant split and the quick-add form keep working.

   Why Liquid could not do this: recommendations are only reachable through the
   Recommendations API, and the previous rail matched on vendor and shared
   collection, which cannot know what customers buy together. */
(function () {
  var SECTION = 'cart-recommendations';
  var TARGET_COUNT = 10;
  /* Seed from at most this many cart lines. Each seed is a network round trip,
     and the first few lines are enough to characterise a basket. */
  var MAX_SEEDS = 3;
  var CONTAINER = '.product-feature-collection__scroll-container';

  var running = false;
  var lastKey = null;

  function fetchIntent(productId, intent, limit) {
    var url = '/recommendations/products?section_id=' + SECTION +
              '&product_id=' + productId +
              '&intent=' + intent +
              '&limit=' + limit;
    return fetch(url)
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html) return [];
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return Array.prototype.slice.call(
          doc.querySelectorAll('.product-feature-collection__item')
        );
      })
      .catch(function () { return []; });
  }

  /* Walk a batch of cards into `picked`, skipping anything already in the cart
     or already chosen. Same-brand cards go to a separate bucket that is only
     drawn on once the rail runs short of everything else.

     Fullness is measured on the cross-brand bucket alone, so a seed that
     returns nothing but its own brand does not stop the next seed from being
     tried. */
  function take(cards, picked, inCart, cartVendors) {
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var handle = card.getAttribute('data-product-handle');
      if (!handle || inCart[handle] || picked.handles[handle]) continue;
      picked.handles[handle] = true;

      var vendor = card.getAttribute('data-product-vendor') || '';
      if (vendor && cartVendors[vendor]) {
        picked.sameBrand.push(card);
      } else {
        picked.cards.push(card);
      }
    }
    return picked.cards.length >= TARGET_COUNT;
  }

  /* Cross-brand first, same-brand only to fill the remaining slots. */
  function finalOrder(picked) {
    var out = picked.cards.slice(0, TARGET_COUNT);
    for (var i = 0; i < picked.sameBrand.length && out.length < TARGET_COUNT; i++) {
      out.push(picked.sameBrand[i]);
    }
    return out;
  }

  function seedsFrom(cart) {
    /* Most recently added first: that line is what the shopper is thinking
       about, so its pairings lead the rail. */
    var ids = [];
    for (var i = cart.items.length - 1; i >= 0 && ids.length < MAX_SEEDS; i--) {
      var id = cart.items[i].product_id;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    }
    return ids;
  }

  function chain(ids, intent, picked, inCart, cartVendors) {
    /* Sequential rather than Promise.all: usually the first seed fills the rail
       and the rest are never requested. */
    var i = 0;
    function next() {
      if (i >= ids.length || picked.cards.length >= TARGET_COUNT) {
        return Promise.resolve();
      }
      var id = ids[i++];
      return fetchIntent(id, intent, TARGET_COUNT).then(function (cards) {
        take(cards, picked, inCart, cartVendors);
        return next();
      });
    }
    return next();
  }

  function build() {
    var container = document.querySelector(CONTAINER);
    if (!container || running) return;

    running = true;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) return;

        /* Skip the work when the basket has not changed since the last run -
           the drawer emits open/update events for things that do not affect
           which recommendations apply. */
        var key = cart.items.map(function (i) { return i.product_id; }).join(',');
        if (key === lastKey) return;
        lastKey = key;

        var inCart = {};
        var cartVendors = {};
        cart.items.forEach(function (i) {
          if (i.handle) inCart[i.handle] = true;
          if (i.vendor) cartVendors[i.vendor] = true;
        });

        var picked = { cards: [], sameBrand: [], handles: {} };
        var ids = seedsFrom(cart);

        return chain(ids, 'complementary', picked, inCart, cartVendors)
          .then(function () {
            if (picked.cards.length >= TARGET_COUNT) return;
            return chain(ids, 'related', picked, inCart, cartVendors);
          })
          .then(function () {
            /* Nothing from either intent: leave the Liquid rail alone rather
               than empty the section. */
            var ordered = finalOrder(picked);
            if (!ordered.length) return;

            var target = document.querySelector(CONTAINER);
            if (!target) return;
            target.innerHTML = '';
            ordered.forEach(function (card) { target.appendChild(card); });

            /* The slider's progress bar and arrows measure the rail's width, so
               they have to remeasure after the swap. */
            document.dispatchEvent(new CustomEvent('theme:cart:update', { bubbles: true }));
          });
      })
      .catch(function () { /* leave the Liquid rail in place */ })
      .then(function () { running = false; });
  }

  /* The drawer renders its contents fresh each time it opens, so the rail is
     rebuilt on open and whenever the cart changes. lastKey keeps repeat events
     from refetching the same basket. */
  ['theme:cart:open', 'theme:cart:loaded', 'theme:cart:added'].forEach(function (evt) {
    document.addEventListener(evt, function () { setTimeout(build, 120); });
  });

  document.addEventListener('DOMContentLoaded', function () { setTimeout(build, 600); });
})();
